import React, { useState } from 'react';
import { recipeAPI } from '../services/api';

// Manual Recipe Entry Form Component
function ManualRecipeForm({ formData, setFormData, listId, onClose, onImport, setLoading, setError, loading }) {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { quantity: '', item: '' }]
    }));
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData(prev => ({ ...prev, instructions: newInstructions }));
  };

  const addInstruction = () => {
    setFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const removeInstruction = (index) => {
    setFormData(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.title.trim()) {
      setError('Recipe title is required');
      return;
    }

    setLoading(true);

    try {
      // Format ingredients with quantities
      const formattedIngredients = formData.ingredients
        .filter(ing => ing.item.trim())
        .map(ing => {
          const item = ing.item.trim();
          const quantity = ing.quantity.trim();
          return quantity ? `${quantity} ${item}` : item;
        });

      // Filter out empty instructions
      const formattedInstructions = formData.instructions.filter(inst => inst.trim());

      await recipeAPI.create({
        list_id: listId,
        title: formData.title,
        description: formData.description,
        servings: formData.servings,
        prep_time: formData.prep_time,
        cook_time: formData.cook_time,
        total_time: formData.total_time,
        ingredients: formattedIngredients,
        instructions: formattedInstructions
      });
      
      onImport();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create recipe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
      <div className="form-group">
        <label>Recipe Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          placeholder="e.g., Chocolate Chip Cookies"
        />
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Brief description of the recipe"
          rows="3"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
        <div className="form-group">
          <label>Servings</label>
          <input
            type="text"
            name="servings"
            value={formData.servings}
            onChange={handleChange}
            placeholder="e.g., 4"
          />
        </div>
        <div className="form-group">
          <label>Prep Time</label>
          <input
            type="text"
            name="prep_time"
            value={formData.prep_time}
            onChange={handleChange}
            placeholder="e.g., 15 min"
          />
        </div>
        <div className="form-group">
          <label>Cook Time</label>
          <input
            type="text"
            name="cook_time"
            value={formData.cook_time}
            onChange={handleChange}
            placeholder="e.g., 30 min"
          />
        </div>
      </div>

      <div className="modal-section">
        <h3>Ingredients</h3>
        {formData.ingredients.map((ingredient, index) => (
          <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              value={ingredient.quantity}
              onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
              placeholder="Quantity (e.g., 2 cups)"
              style={{ width: '150px', flexShrink: 0 }}
            />
            <input
              type="text"
              value={ingredient.item}
              onChange={(e) => handleIngredientChange(index, 'item', e.target.value)}
              placeholder="Ingredient name"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => removeIngredient(index)}
              style={{ padding: '0.5rem 1rem' }}
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-secondary" onClick={addIngredient}>
          + Add Ingredient
        </button>
      </div>

      <div className="modal-section">
        <h3>Instructions</h3>
        {formData.instructions.map((instruction, index) => (
          <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ 
              background: 'var(--primary-blue)', 
              color: 'white', 
              borderRadius: '50%', 
              width: '28px', 
              height: '28px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexShrink: 0,
              fontWeight: 'bold'
            }}>
              {index + 1}
            </span>
            <textarea
              value={instruction}
              onChange={(e) => handleInstructionChange(index, e.target.value)}
              placeholder={`Step ${index + 1}`}
              style={{ flex: 1, minHeight: '60px' }}
            />
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => removeInstruction(index)}
              style={{ padding: '0.5rem 1rem' }}
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-secondary" onClick={addInstruction}>
          + Add Step
        </button>
      </div>

      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-success" disabled={loading}>
          {loading ? <span className="loading-spinner"></span> : 'Create Recipe'}
        </button>
      </div>
    </form>
  );
}

function ImportModal({ listId, onClose, onImport }) {
  const [activeTab, setActiveTab] = useState('manual');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  
  // Manual entry form state
  const [manualForm, setManualForm] = useState({
    title: '',
    description: '',
    servings: '',
    prep_time: '',
    cook_time: '',
    total_time: '',
    ingredients: [{ quantity: '', item: '' }],
    instructions: ['']
  });

  const handleUrlImport = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await recipeAPI.importFromUrl(url, listId);
      onImport();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import recipe');
    } finally {
      setLoading(false);
    }
  };

  const handleFileImport = async (selectedFile) => {
    if (!selectedFile) return;

    setError('');
    setLoading(true);

    try {
      await recipeAPI.importFromFile(selectedFile, listId);
      onImport();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import recipe');
    } finally {
      setLoading(false);
      setFile(null);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    if (selectedFile) {
      handleFileImport(selectedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      handleFileImport(droppedFile);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import Recipe</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="import-tabs">
          <button
            className={`import-tab ${activeTab === 'manual' ? 'active' : ''}`}
            onClick={() => setActiveTab('manual')}
          >
            Manual Entry
          </button>
          <button
            className={`import-tab ${activeTab === 'url' ? 'active' : ''}`}
            onClick={() => setActiveTab('url')}
          >
            From URL
          </button>
          <button
            className={`import-tab ${activeTab === 'file' ? 'active' : ''}`}
            onClick={() => setActiveTab('file')}
          >
            From File
          </button>
        </div>

        {activeTab === 'manual' ? (
          <ManualRecipeForm 
            formData={manualForm}
            setFormData={setManualForm}
            listId={listId}
            onClose={onClose}
            onImport={onImport}
            setLoading={setLoading}
            setError={setError}
            loading={loading}
          />
        ) : activeTab === 'url' ? (
          <form onSubmit={handleUrlImport}>
            <div className="form-group">
              <label>Recipe URL</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/recipe"
                required
              />
              <small>Paste a link to any recipe from popular cooking websites</small>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? <span className="loading-spinner"></span> : 'Import Recipe'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div
              className={`file-upload-area ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                accept="image/*,.pdf,.docx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {loading ? (
                <div>
                  <div className="loading-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto' }}></div>
                  <p>Extracting recipe...</p>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: '3rem', margin: '0' }}>📄</p>
                  <p style={{ fontWeight: 'bold', marginTop: '1rem' }}>
                    Drop your image, PDF, or DOCX here
                  </p>
                  <p style={{ color: '#666' }}>
                    or click to browse
                  </p>
                  <small style={{ color: '#999' }}>
                    Supports JPG, PNG, PDF, DOCX
                  </small>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportModal;
